import * as React from 'react';
import { cloneDeep, isUndefined } from 'lodash';

import {
  createControl,
  IControlError,
  IControlSchema,
  RuleOptions,
} from '../../core/Controls';
import { Dictionary } from '../../types/Dictionary';

import { Component, uid, valueObjectToArray } from '../helpers/Utils';
import { IDispatcher } from '../helpers/Dispatcher';

import Field from './components/Field';
import RulesManager from '../../core/validation/RulesManager';

export interface IGroupSchema extends IControlSchema {
  columns: Array<IControlSchema>;
  disabled?: boolean;
  visible?: boolean;
  multiple?: boolean;
  value: any;
  extra?: {
    lengthFromId: string;
    subtitleNumber: boolean;
    subtitlePrefix: string;
  };
}

interface Props {
  schema: IGroupSchema;
  api: any;
  dispatcher?: IDispatcher;
  errors: Dictionary<IControlError | undefined>;
  values: any[];
  visited: Dictionary<boolean>;
  opts: any;
  allVisited: boolean;
}
type TGroups = {
  [key in string]: Record<string, any>;
};

interface State {
  groups: TGroups;
}

class Group extends Component<Props, State> {
  state = {
    groups: {},
  };

  componentDidMount() {
    const {
      schema: { value },
    } = this.props;

    if (value) {
      this.setState({ groups: value });
      return;
    }

    this.addGroup();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (prevProps.values !== this.props.values) {
      const {
        schema: { extra },
      } = this.props;

      const groupsKeys = Object.keys(prevState.groups);

      if (extra?.lengthFromId) {
        const addictionLengths = this.findAddictionCtrlLength(
          extra?.lengthFromId,
        );

        if (groupsKeys.length === addictionLengths) {
          return;
        }
        if (addictionLengths > groupsKeys.length) {
          this.addGroup();
        } else {
          this.deleteGroup(groupsKeys[groupsKeys.length - 1])();
        }
      }
    }
  }

  dynamicGroupID = () => `Group.${uid()}.${this.props.schema.id}`;

  createCloneGroup = (groupID: string, rowIndex: number) => {
    const { schema, api, dispatcher, errors, visited, allVisited, values } =
      this.props;
    const cloneDispatcher = cloneDeep(dispatcher);

    cloneDispatcher.onBlur = this.onBlur(groupID);
    cloneDispatcher.onAddValueRefresh = this.onAddValueRefresh(groupID);
    cloneDispatcher.onLoadFile = this.onLoadFile(groupID);
    cloneDispatcher.pullValue = this.pullValue(groupID);
    const ruleManager = new RulesManager();

    return (_values?: any, _errors?: any): Array<React.ReactElement> => {
      // скрыл потому что вроде как до этого рендера происходит валидация, а тут она плоха тем что values не содержит достаточной информации
      const columns = ruleManager.round(schema.columns, [
        ...valueObjectToArray(_values),
        ...values,
      ]);

      return columns.map((column) => {
        let cloneI18: Record<string, unknown> = {};
        column.disableNumber = schema.disableNumber;

        if (!column.disabled) {
          column.disabled = schema.disabled;
        }

        // TODO?!?!
        // 	column.controlValue = values?.[column?.id];

        // i18n control
        if (
          column?.extra?.i18n &&
          column.type.toUpperCase() !== 'I18N' &&
          column.type.toUpperCase() !== 'I18N_COMPLEX'
        ) {
          const i18nControl = columns?.find(
            (item) =>
              column.extra.i18n.includes(item.id) &&
              (item.type === 'i18n' || item.type === 'i18n_complex'),
          );

          if (i18nControl) {
            cloneI18.i18nControls = {
              ...i18nControl,
              value: _values?.[i18nControl?.id] ?? null,
              disabled: schema.disabled,
            };
          }
        }

        //condition for rendering fields
        if (
          column &&
          column.type.toUpperCase() !== 'I18N' &&
          column.type.toUpperCase() !== 'I18N_COMPLEX'
        ) {
          const element = createControl(
            // @ts-ignore
            (column = {
              ...column,
              value: _values?.[column?.id],
              extra: {
                ...(column?.extra ?? {}),
                ...cloneI18,
              },
            }),
            api,
            cloneDispatcher,
            errors?.[schema.id]?.message?.[groupID] ?? null,
            _values?.[column?.id],
            visited,
            allVisited,
            this.props.opts,
            undefined,
            undefined,
            undefined,
            rowIndex,
          );

          return (
            element &&
            React.cloneElement(element, {
              key: `Group.${column.id}`,
              values: _values?.[column?.id],
            })
          );
        }
      });
    };
  };

  addGroup = (event?: React.MouseEvent<HTMLButtonElement>): void => {
    event?.preventDefault();
    const { schema, dispatcher } = this.props;
    const groupID = this.dynamicGroupID();

    this.setState(({ groups }) => {
      const _copyGroups = {
        ...groups,
        [groupID]: {},
      };

      // if (!schema?.disabled) {
      // 	dispatcher.onBlur(schema.id, _copyGroups, true, undefined);
      // }

      return {
        groups: { ..._copyGroups },
      };
    });
  };

  deleteGroup =
    (groupID: string) =>
    (event?: React.MouseEvent<HTMLButtonElement>): void => {
      event?.preventDefault();
      const { schema, dispatcher } = this.props;

      this.setState(({ groups }) => {
        const _copyGroups = { ...groups };
        delete _copyGroups[groupID];

        dispatcher.onBlur(schema.id, _copyGroups, true, undefined);

        return {
          groups: { ..._copyGroups },
        };
      });
    };

  onBlur = (groupID) => (controlID: string, value: any) => {
    const { schema, dispatcher } = this.props;

    this.setState(({ groups }) => {
      const _copyGroups = {
        ...groups,
        [groupID]: {
          ...groups[groupID],
          [controlID]: value,
        },
      };

      dispatcher.onBlur(schema.id, _copyGroups, true, undefined);

      return {
        groups: { ..._copyGroups },
      };
    });
  };

  onAddValueRefresh = (groupID: string) => (controlID: string, value: any) => {
    const { schema, dispatcher } = this.props;

    this.setState(({ groups }) => {
      const _copyGroups = {
        ...groups,
        [groupID]: {
          ...groups[groupID],
          [controlID]: value,
        },
      };

      dispatcher.onAddValueRefresh(schema.id, _copyGroups, undefined);

      return {
        groups: { ..._copyGroups },
      };
    });
  };

  pullValue = (groupID: string) => (controlID: string) =>
    this.state.groups[groupID]?.[controlID];

  onLoadFile = (groupID: string) => (controlID: string, state: object) => {
    const { schema, dispatcher } = this.props;

    this.setState(({ groups }) => {
      const _copyGroups = {
        ...groups,
        [groupID]: {
          ...groups[groupID],
          [controlID]: state,
        },
      };

      dispatcher.onBlur(schema.id, _copyGroups, true, undefined);

      return {
        groups: { ..._copyGroups },
      };
    });
  };

  findAddictionCtrlLength = (controlID: string): number => {
    const { values } = this.props;
    const ctrl = values?.find((_ctrl) => controlID === _ctrl?.controlid);

    if (ctrl?.value?.beginDate || ctrl?.value?.finishDate) {
      return 1;
    }
    if (Array.isArray(ctrl?.value)) {
      return ctrl.value.length;
    }
    return 1;
  };

  render() {
    const { schema } = this.props;
    const isVisible = isUndefined(schema.visible) ? true : schema.visible;
    const groupsLength = Object.keys(this.state.groups).length;
    const isAddictionGroup = !!schema?.extra;
    const subTitlePrefix = schema?.extra?.subtitlePrefix;

    return (
      isVisible && (
        <Field {...schema} id={`group-${schema.id}`} key={`group-${schema.id}`}>
          {Object.keys(this.state?.groups)?.map((groupID, idx) => (
            <div className="groups__item" key={groupID}>
              {subTitlePrefix && <h6>{`${subTitlePrefix} ${idx + 1}`}</h6>}

              {this.createCloneGroup(groupID, idx)(this.state.groups[groupID])}

              {schema?.multiple && !isAddictionGroup && !schema?.disabled && (
                <div className="btn-group mb-4 d-flex justify-content-end">
                  {groupsLength - 1 === idx && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={this.addGroup}
                    >
                      Добавить
                    </button>
                  )}
                  {groupsLength > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={this.deleteGroup(groupID)}
                    >
                      Удалить
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Field>
      )
    );
  }
}

export function createGroup(
  schema: IControlSchema,
  api: any,

  dispatcher: IDispatcher,
  errors: Dictionary<IControlError | undefined>,
  values: any[],
  visited: Dictionary<boolean>,
  opts: any,
  allVisited: boolean,
): React.ReactElement<any> {
  return (
    <Group
      schema={schema as IGroupSchema}
      api={api}
      dispatcher={dispatcher}
      errors={errors}
      values={values}
      visited={visited}
      allVisited={allVisited}
      opts={opts}
    />
  );
}
